<template>
  <div class="print:hidden mx-1" v-if="showFilterPane">
    <transition-group
      tag="div"
      enter-active-class="duration-400 ease-out"
      enter-from-class="transform opacity-0 -translate-y-10"
      enter-to-class="opacity-100"
      leave-active-class="duration-400 ease-out absolute"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0 -translate-y-10"
      move="transition-all duration-400 ease"
    >
      <div class="transition-all duration-300 ease-out" key="company-info">
        <div
          class="flex flex-row justify-between items-center pb-0.5 border-b mx-1.5 border-b-gray-400"
        >
          <h3 class="text-sm">Approval Info:</h3>
          <base-button-affirmative @click="resetFilters" class="text-xs -py-2"
            >Reset</base-button-affirmative
          >
        </div>
        <div
          class="pt-1 grid gap-x-1 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8"
          id="company-info"
        >
          <base-filter :active="!!companyFilter">
            <template v-slot:label> Company </template>
            <template v-slot:widget>
              <TypeAheadSingleSelectBox
                :options="companyOptions"
                :default-value="companyFilter"
                placeholder="Search companies…"
                no-selection-message="Any company"
                @update-value="(v) => (companyFilter = v || '')"
              />
            </template>
          </base-filter>

          <base-filter :active="!!partNameFilter">
            <template v-slot:label>Part Name</template>
            <template v-slot:widget>
              <input
                v-model="partNameFilter"
                class="h-full px-2 w-full rounded-b-md shadow-md"
                type="text"
              />
            </template>
          </base-filter>
          <base-filter :active="!!makeFilter">
            <template v-slot:label>Make</template>
            <template v-slot:widget>
              <input
                v-model="makeFilter"
                class="h-full px-2 w-full rounded-b-md shadow-md"
                type="text"
                placeholder="comma-separated"
              />
            </template>
          </base-filter>
          <base-filter :active="!!excludeMakeFilter">
            <template v-slot:label>Exclude Make</template>
            <template v-slot:widget>
              <input
                v-model="excludeMakeFilter"
                class="h-full px-2 w-full rounded-b-md shadow-md"
                type="text"
                placeholder="comma-separated"
              />
            </template>
          </base-filter>
          <base-filter :active="!!modelFilter">
            <template v-slot:label>Model</template>
            <template v-slot:widget>
              <input
                v-model="modelFilter"
                class="h-full px-2 w-full rounded-b-md shadow-md"
                type="text"
              />
            </template>
          </base-filter>

          <base-filter :active="!!supplementDateBeforeFilter || !!supplementDateAfterFilter">
            <template v-slot:label>Supplement Date</template>
            <template v-slot:widget>
              <div class="flex h-full gap-1">
                <input
                  placeholder="after"
                  v-model="supplementDateAfterFilter"
                  class="h-full px-2 w-1/2 rounded-b-md"
                  type="date"
                />
                <input
                  placeholder="before"
                  v-model="supplementDateBeforeFilter"
                  class="h-full px-2 w-1/2 rounded-b-md"
                  type="date"
                />
              </div>
            </template>
          </base-filter>
          <base-filter :active="collectionTagFilter.length > 0">
            <template v-slot:label>Collection Tag</template>
            <template v-slot:widget>
              <TypeAheadMultiSelectBox
                :options="collectionTagOptions"
                :default-value="collectionTagFilter"
                placeholder="Search tags…"
                no-selection-message="Any tag"
                @update-value="(v) => collectionTagFilter = v"
              />
            </template>
          </base-filter>
          <base-filter :active="excludeCollectionTagFilter.length > 0">
            <template v-slot:label>Exclude Collection Tag</template>
            <template v-slot:widget>
              <TypeAheadMultiSelectBox
                :options="collectionTagOptions"
                :default-value="excludeCollectionTagFilter"
                placeholder="Search tags…"
                no-selection-message="None excluded"
                @update-value="(v) => excludeCollectionTagFilter = v"
              />
            </template>
          </base-filter>
          <base-filter :active="campaignFilter.length > 0">
            <template v-slot:label>Campaign</template>
            <template v-slot:widget>
              <TypeAheadMultiSelectBox
                :options="campaignOptions"
                :default-value="campaignFilter"
                placeholder="Search campaigns…"
                no-selection-message="Any campaign"
                @update-value="(v) => campaignFilter = v"
              />
            </template>
          </base-filter>
          <base-filter :active="excludeCampaignFilter.length > 0">
            <template v-slot:label>Exclude Campaign</template>
            <template v-slot:widget>
              <TypeAheadMultiSelectBox
                :options="campaignOptions"
                :default-value="excludeCampaignFilter"
                placeholder="Search campaigns…"
                no-selection-message="None excluded"
                @update-value="(v) => excludeCampaignFilter = v"
              />
            </template>
          </base-filter>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script setup>
// imports

import { ref, computed, defineEmits, defineProps, watch, reactive } from "vue";
import { useStore } from "vuex";

import BaseListBox from "../BaseListBox.vue";
import TypeAheadMultiSelectBox from "@/components/base-components/TypeAheadMultiSelectBox.vue";
import TypeAheadSingleSelectBox from "@/components/base-components/TypeAheadSingleSelectBox.vue";

// constants/variables
const store = useStore();
const showFilterPane = computed(() => store.getters['research/showFilterPane'])

const props = defineProps([
  "companyOptions",
  "visibleObject",
  // {label, value} arrays sourced from the parent — we don't fetch
  // these here so PMAFilters stays presentational and the parent
  // controls cache + lifecycle.
  "collectionTagOptions",
  "campaignOptions",
]);
const emit = defineEmits([
  "change-referral-filters",
  "set-changed-referral-filters",
  "reset-info",
]);

const companyFilter = ref("");
const makeFilter = ref("");
const excludeMakeFilter = ref("");
const modelFilter = ref("");
const partNameFilter = ref("");
const supplementDateAfterFilter = ref(null)
const supplementDateBeforeFilter = ref(null)
// Multi-select filters store arrays of numeric IDs.
const collectionTagFilter = ref([])
const excludeCollectionTagFilter = ref([])
const campaignFilter = ref([])
const excludeCampaignFilter = ref([])
const supplimentDateFilter = ref([]);

const dateOptions = ref([
  { value: "clear", label: "------------" },
  { value: 365, label: "Last Year" },
  { value: 1095, label: "Last 3 Years" },
  { value: 1825, label: "Last 5 Years" },
  { value: 3650, label: "Last 10 Years" },
]);

// computed values

const filters = computed(() => {
  return {
    companyFilter: companyFilter,
    makeFilter: makeFilter,
    excludeMakeFilter: excludeMakeFilter,
    modelFilter: modelFilter,
    partNameFilter: partNameFilter,
    supplementDateBeforeFilter: supplementDateBeforeFilter,
    supplementDateAfterFilter: supplementDateAfterFilter,
    collectionTagFilter: collectionTagFilter,
    excludeCollectionTagFilter: excludeCollectionTagFilter,
    campaignFilter: campaignFilter,
    excludeCampaignFilter: excludeCampaignFilter,
  };
});

// functions

// function setMakeFilter(event) {
//   makeFilter.value = event.target.value;
//   emitChangeApprovalFiltersAndSetChanged();
// }

// function setModelFilter(event) {
//   modelFilter.value = event.target.value;
//   emitChangeApprovalFiltersAndSetChanged();
// }

// function setPartNameFilter(event) {
//   partNameFilter.value = event.target.value;
//   emitChangeApprovalFiltersAndSetChanged();
// }

// function setSupplementDateBeforeFilter(event){
//   supplementDateBeforeFilter.value = event.target.value
//   emitChangeApprovalFiltersAndSetChanged();
// }

// function setSupplementDateAfterFilter(event){
//   supplementDateAfterFilter.value = event.target.value
//   emitChangeApprovalFiltersAndSetChanged();
// }

function setSupplimentDateFilter(target) {
  if (target === "clear") {
    supplimentDateFilter.value = [];
  } else {
    supplimentDateFilter.value = target;
  }
  emitChangeApprovalFiltersAndSetChanged();
}

function emitChangeApprovalFiltersAndSetChanged() {
  emit("change-referral-filters", filters);
  emit("set-changed-referral-filters");
}

function resetFilters() {
  companyFilter.value = "";
  modelFilter.value = "";
  makeFilter.value = "";
  excludeMakeFilter.value = "";
  partNameFilter.value = "";
  supplementDateBeforeFilter.value = null;
  supplementDateAfterFilter.value = null;
  supplimentDateFilter.value = [];
  collectionTagFilter.value = [];
  excludeCollectionTagFilter.value = [];
  campaignFilter.value = [];
  excludeCampaignFilter.value = [];
  emit("reset-info");
  emitChangeApprovalFiltersAndSetChanged();
}

const debounceTimer = ref(null)

watch(filters, () => {
  clearTimeout(debounceTimer.value)
  debounceTimer.value = setTimeout(() => {
    emitChangeApprovalFiltersAndSetChanged()
  }, 400)
}, {deep: true})

</script>
